import { Download, FileArchive } from "lucide-react";

const DESIGNS = [
  { slug: "pinky-promise", title: "Pinky Promise", thumb: "/social/generated/final-03-pinky-0.png" },
  { slug: "pinky-clear", title: "Pinky Promise (Clear Pose)", thumb: "/social/generated/pinky-v3-clear-pose-0.png" },
  { slug: "habit-green-i", title: "Habit Loop — Green i", thumb: "/social/generated/final-04-habit-green-i-0.png" },
  { slug: "habit-loop", title: "Habit Loop", thumb: "/social/generated/final-v3-04-habit.png" },
  { slug: "prompt-mirror", title: "Prompt Mirror", thumb: "/social/generated/final-v3-01-prompt.png" },
  { slug: "handshake", title: "Human × AI Handshake", thumb: "/social/generated/final-v3-02-handshake.png" },
  { slug: "janus-two-face", title: "Janus (Two-Face)", thumb: "/social/generated/final-v3-05-janus.png" },
  { slug: "third-eye", title: "Third Eye", thumb: "/social/generated/final-v3-06-thirdeye.png" },
  { slug: "whisper", title: "Whisper", thumb: "/social/generated/final-v3-07-whisper.png" },
  { slug: "oracle-tarot", title: "Oracle Tarot", thumb: "/social/generated/final-v3-08-oracle.png" },
  { slug: "commandments-tablet", title: "Commandments Tablet", thumb: "/social/generated/commandments-tablet-0.png" },
  { slug: "whoracle-mascot", title: "Whoracle Mascot", thumb: "/social/generated/whoracle-logo-v4.png" },
];

// Checkered pattern behind transparent thumb so users see the transparency
const checkerBg = {
  backgroundImage:
    "linear-gradient(45deg, #111 25%, transparent 25%), linear-gradient(-45deg, #111 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #111 75%), linear-gradient(-45deg, transparent 75%, #111 75%)",
  backgroundSize: "20px 20px",
  backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0",
  backgroundColor: "#1a1a1a",
};

export const PrintReady = () => {
  return (
    <div className="min-h-screen bg-black text-green-400 font-mono px-4 sm:px-8 py-12" data-testid="print-ready-page">
      <div className="max-w-6xl mx-auto">
        <header className="mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-green-600">/print-ready</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl mt-2 text-green-300">Print-Ready Assets</h1>
          <p className="mt-4 text-green-500 max-w-2xl text-sm sm:text-base leading-relaxed">
            High-resolution files formatted for Printful, Printify, Gelato and other print-on-demand services.
            Each PNG is <span className="text-green-300">4500&thinsp;×&thinsp;4500&nbsp;px</span> at{" "}
            <span className="text-green-300">300&nbsp;DPI</span>. Two versions per design:
          </p>
          <ul className="mt-3 text-sm text-green-500 space-y-1">
            <li>· <span className="text-green-300">Solid</span> — original black-square background</li>
            <li>· <span className="text-green-300">Transparent</span> — background removed, ideal for coloured garments</li>
          </ul>
        </header>

        <a
          href="/print-ready/saimonsays-print-ready.zip"
          download
          data-testid="download-all-zip"
          className="inline-flex items-center gap-3 border border-green-500 text-green-300 hover:bg-green-500 hover:text-black transition-colors px-5 py-3 rounded-none mb-12"
        >
          <FileArchive size={18} />
          <span className="text-sm">Download all 24 files as ZIP (~132 MB)</span>
        </a>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {DESIGNS.map((d) => (
            <div
              key={d.slug}
              className="border border-green-900 hover:border-green-500 transition-colors p-4 flex flex-col"
              data-testid={`design-card-${d.slug}`}
            >
              <h2 className="text-lg text-green-300 leading-snug mb-3">{d.title}</h2>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <div className="aspect-square bg-black overflow-hidden border border-green-900">
                    <img src={d.thumb} alt={`${d.title} solid`} className="w-full h-full object-contain" loading="lazy" />
                  </div>
                  <p className="text-[10px] text-green-600 mt-1 text-center uppercase tracking-widest">Solid</p>
                </div>
                <div>
                  <div className="aspect-square overflow-hidden border border-green-900" style={checkerBg}>
                    <img
                      src={`/print-ready/${d.slug}-transparent-300dpi.png`}
                      alt={`${d.title} transparent`}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <p className="text-[10px] text-green-600 mt-1 text-center uppercase tracking-widest">Transparent</p>
                </div>
              </div>

              <p className="text-xs text-green-600 mb-3">4500 px · 300 DPI · PNG</p>

              <div className="grid grid-cols-2 gap-2 mt-auto">
                <a
                  href={`/print-ready/${d.slug}-300dpi.png`}
                  download
                  data-testid={`download-solid-${d.slug}`}
                  className="inline-flex items-center justify-center gap-1 border border-green-700 text-green-400 hover:bg-green-700 hover:text-black transition-colors py-2 text-[10px] uppercase tracking-widest"
                >
                  <Download size={12} />
                  Solid
                </a>
                <a
                  href={`/print-ready/${d.slug}-transparent-300dpi.png`}
                  download
                  data-testid={`download-transparent-${d.slug}`}
                  className="inline-flex items-center justify-center gap-1 border border-green-500 text-green-300 hover:bg-green-500 hover:text-black transition-colors py-2 text-[10px] uppercase tracking-widest"
                >
                  <Download size={12} />
                  Transparent
                </a>
              </div>
            </div>
          ))}
        </div>

        <section className="mt-16 border-t border-green-900 pt-8">
          <h3 className="text-green-300 text-xl mb-3">How to use with Printful</h3>
          <ol className="text-sm text-green-500 space-y-2 list-decimal list-inside">
            <li>Tap a <span className="text-green-300">Transparent</span> button and save the image to your phone.</li>
            <li>In Printful, pick a product → placement (e.g. Left chest, Back).</li>
            <li>Upload the file — the low-resolution warning will be gone.</li>
            <li>For light garments the Solid version also works; use Transparent for coloured/dark garments.</li>
          </ol>
        </section>
      </div>
    </div>
  );
};

export default PrintReady;
